/**
 * Pure diff for two Formio form schemas. Computes added / removed / modified /
 * reordered components between an audit event's `beforeValue` and `afterValue`.
 * Consumed by `<FormSchemaDiff>` in the audit modal. JSON-only input, no React,
 * no I/O.
 */

export interface FieldRef {
  path: string;
  key: string;
  type: string;
  label: string;
}

export interface ModifiedProperty {
  property: string;
  before: unknown;
  after: unknown;
}

export interface ModifiedField extends FieldRef {
  changes: ModifiedProperty[];
}

export interface ReorderedField extends FieldRef {
  beforeIndex: number;
  afterIndex: number;
}

export interface SchemaDiff {
  added: FieldRef[];
  removed: FieldRef[];
  modified: ModifiedField[];
  reordered: ReorderedField[];
}

const EMPTY_DIFF: SchemaDiff = { added: [], removed: [], modified: [], reordered: [] };

const BASE_PROPERTIES = [
  'label',
  'key',
  'type',
  'placeholder',
  'description',
  'tooltip',
  'defaultValue',
  'hidden',
  'disabled',
  'multiple',
  'prefix',
  'suffix',
  'customClass',
  'validate.required',
  'validate.minLength',
  'validate.maxLength',
  'validate.pattern',
  'validate.custom',
  'conditional.show',
  'conditional.when',
  'conditional.eq',
];

const PER_TYPE_PROPERTIES: Record<string, string[]> = {
  textarea: ['rows', 'wysiwyg', 'autoExpand', 'inputFormat'],
  select: ['data.values'],
  radio: ['data.values'],
  selectboxes: ['data.values'],
  number: ['decimalLimit', 'requireDecimal', 'delimiter', 'validate.min', 'validate.max'],
  currency: ['decimalLimit', 'requireDecimal', 'delimiter', 'validate.min', 'validate.max'],
  file: ['storage', 'filePattern', 'fileMaxSize'],
  datetime: ['format', 'enableDate', 'enableTime', 'datePicker.maxDate', 'datePicker.minDate'],
  panel: ['title', 'collapsible'],
  fieldset: ['legend'],
};

interface FlatNode {
  path: string;
  key: string;
  type: string;
  label: string;
  raw: Record<string, unknown>;
  index: number;
}

// Walk every component into a path-keyed map. Recurses into all container
// shapes (`components`, `columns[].components`, `rows[][].components`) without
// gating on `type` — tab-objects (children of `tabs`) have no `type` field.
function flatten(components: unknown, pathPrefix: string): Map<string, FlatNode> {
  const out = new Map<string, FlatNode>();
  if (!Array.isArray(components)) return out;
  components.forEach((c, idx) => {
    if (c === null || typeof c !== 'object') return;
    const obj = c as Record<string, unknown>;
    const key = typeof obj.key === 'string' ? obj.key : '';
    const type = typeof obj.type === 'string' ? obj.type : '';
    if (!key) return;
    const path = pathPrefix ? `${pathPrefix}.${key}` : key;
    const label =
      typeof obj.label === 'string' && obj.label.length > 0 ? obj.label : key;
    out.set(path, { path, key, type, label, raw: obj, index: idx });

    if (Array.isArray(obj.components)) {
      for (const [k, v] of flatten(obj.components, path)) out.set(k, v);
    }
    if (Array.isArray(obj.columns)) {
      for (const col of obj.columns) {
        if (
          col &&
          typeof col === 'object' &&
          Array.isArray((col as Record<string, unknown>).components)
        ) {
          for (const [k, v] of flatten(
            (col as Record<string, unknown>).components,
            path,
          )) {
            out.set(k, v);
          }
        }
      }
    }
    if (Array.isArray(obj.rows)) {
      for (const row of obj.rows) {
        if (!Array.isArray(row)) continue;
        for (const cell of row) {
          if (
            cell &&
            typeof cell === 'object' &&
            Array.isArray((cell as Record<string, unknown>).components)
          ) {
            for (const [k, v] of flatten(
              (cell as Record<string, unknown>).components,
              path,
            )) {
              out.set(k, v);
            }
          }
        }
      }
    }
  });
  return out;
}

// Dotted-path getter (e.g. `validate.required`). Returns undefined on miss.
function getByPath(obj: Record<string, unknown>, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, part) => {
    if (acc === null || acc === undefined || typeof acc !== 'object') return undefined;
    return (acc as Record<string, unknown>)[part];
  }, obj);
}

// JSON-only deep equality. No Date / Map / Set / NaN handling — Formio
// schemas are pure JSON. Arrays are index-wise (order matters, e.g. `data.values`).
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    return a.every((v, i) => deepEqual(v, b[i]));
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const ak = Object.keys(ao);
  const bk = Object.keys(bo);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => deepEqual(ao[k], bo[k]));
}

// Base + per-type allow-list of properties to compare. Auto-generated noise
// (`id`, `tabindex`, `widget`, `overlay`, ...) is excluded by omission.
function comparableProperties(type: string): string[] {
  return [...BASE_PROPERTIES, ...(PER_TYPE_PROPERTIES[type] ?? [])];
}

// List allow-listed properties whose values differ between two flat nodes.
function diffNode(before: FlatNode, after: FlatNode): ModifiedProperty[] {
  const props = new Set([
    ...comparableProperties(before.type),
    ...comparableProperties(after.type),
  ]);
  const changes: ModifiedProperty[] = [];
  for (const prop of props) {
    const bv = getByPath(before.raw, prop);
    const av = getByPath(after.raw, prop);
    if (!deepEqual(bv, av)) {
      changes.push({ property: prop, before: bv, after: av });
    }
  }
  return changes;
}

// Drop paths whose ancestor is also in the set — so a wholesale-added panel
// reports the panel itself, not every one of its children.
function stripChildrenOfAncestors(paths: string[]): string[] {
  const set = new Set(paths);
  return paths.filter((p) => {
    const parts = p.split('.');
    for (let i = parts.length - 1; i > 0; i--) {
      if (set.has(parts.slice(0, i).join('.'))) return false;
    }
    return true;
  });
}

// Main entry. Either side may be null (form-create has no `before`, form-delete
// has no `after`) — missing side is treated as `{ components: [] }` so every
// field on the present side surfaces as added (or removed).
export function diffFormSchemas(before: unknown, after: unknown): SchemaDiff {
  const beforeComponents =
    before && typeof before === 'object'
      ? (before as Record<string, unknown>).components
      : null;
  const afterComponents =
    after && typeof after === 'object'
      ? (after as Record<string, unknown>).components
      : null;

  // Bail only when BOTH sides are malformed — single missing side is normal
  // (create / delete events).
  if (!Array.isArray(beforeComponents) && !Array.isArray(afterComponents)) {
    console.warn('[formSchemaDiff] both sides malformed; returning empty diff');
    return EMPTY_DIFF;
  }
  const beforeArr: unknown[] = Array.isArray(beforeComponents) ? beforeComponents : [];
  const afterArr: unknown[] = Array.isArray(afterComponents) ? afterComponents : [];
  const beforeMap = flatten(beforeArr, '');
  const afterMap = flatten(afterArr, '');

  const addedPaths = [...afterMap.keys()].filter((p) => !beforeMap.has(p));
  const removedPaths = [...beforeMap.keys()].filter((p) => !afterMap.has(p));

  const topAdded = stripChildrenOfAncestors(addedPaths);
  const topRemoved = stripChildrenOfAncestors(removedPaths);

  const added: FieldRef[] = topAdded.map((p) => {
    const n = afterMap.get(p)!;
    return { path: n.path, key: n.key, type: n.type, label: n.label };
  });
  const removed: FieldRef[] = topRemoved.map((p) => {
    const n = beforeMap.get(p)!;
    return { path: n.path, key: n.key, type: n.type, label: n.label };
  });

  const sharedPaths = [...beforeMap.keys()].filter((p) => afterMap.has(p));

  const modified: ModifiedField[] = [];
  for (const p of sharedPaths) {
    const bn = beforeMap.get(p)!;
    const an = afterMap.get(p)!;
    const changes = diffNode(bn, an);
    if (changes.length > 0) {
      modified.push({ path: an.path, key: an.key, type: an.type, label: an.label, changes });
    }
  }

  const reordered: ReorderedField[] = [];
  for (const p of sharedPaths) {
    const bn = beforeMap.get(p)!;
    const an = afterMap.get(p)!;
    if (bn.index !== an.index) {
      reordered.push({
        path: an.path,
        key: an.key,
        type: an.type,
        label: an.label,
        beforeIndex: bn.index,
        afterIndex: an.index,
      });
    }
  }

  return { added, removed, modified, reordered };
}
